import { useEffect } from "react"

import mammoth from "mammoth"
import pdfToText from "react-pdftotext"
import * as XLSX from "xlsx"

import { logger } from "@/lib/logger"
import TokenService from "@/services/token-service"

// Skip heavy file parsing for files larger than this (5MB)
// Large files will use an estimated token count instead
const MAX_FILE_SIZE_FOR_PARSING = 5 * 1024 * 1024

// Rough estimate: ~4 chars per token, ~1 byte per char for text files
// For binary files (PDF, DOCX), assume ~2 bytes per char after extraction
const ESTIMATED_TOKENS_PER_BYTE = 0.25

type UseFileContentProps = {
  availableFiles?: File[]
  onTokenCountChange: (tokenCount: number) => void
}

type FileProcessor = (file: File) => Promise<string>

type FileTypeConfig = {
  mimeTypes: string[]
  extensions: string[]
  process: FileProcessor
}

export function useFileContent({ availableFiles, onTokenCountChange }: UseFileContentProps) {
  const tokenService = TokenService.getInstance()

  const readExcelFile: FileProcessor = async (file) => {
    const arrayBuffer = await file.arrayBuffer()
    const workbook = XLSX.read(arrayBuffer)
    return workbook.SheetNames.map((name) => XLSX.utils.sheet_to_csv(workbook.Sheets[name])).join(
      "\n"
    )
  }

  const readJSONFile: FileProcessor = async (file) => {
    const text = await readTxtFile(file)
    try {
      JSON.parse(text)
      return text
    } catch (e) {
      logger.error("Error parsing JSON file:", e as Error)
      return text
    }
  }

  const readPDFFile: FileProcessor = async (file) => {
    return await pdfToText(file)
  }

  const readDocxFile: FileProcessor = async (file) => {
    const fileReader = new FileReader()
    fileReader.readAsArrayBuffer(file)

    const arrayBuffer = await new Promise<ArrayBuffer>((resolve, reject) => {
      fileReader.onload = () => resolve(fileReader.result as ArrayBuffer)
      fileReader.onerror = reject
    })

    const result = await mammoth.extractRawText({ arrayBuffer })
    return result.value
  }

  const readTxtFile: FileProcessor = async (file) => {
    const fileReader = new FileReader()
    fileReader.readAsText(file)

    const text = await new Promise<string>((resolve, reject) => {
      fileReader.onload = () => resolve(fileReader.result as string)
      fileReader.onerror = reject
    })

    return text
  }

  const fileTypeMap: FileTypeConfig[] = [
    {
      mimeTypes: ["application/pdf"],
      extensions: [".pdf"],
      process: readPDFFile,
    },
    {
      mimeTypes: ["application/vnd.openxmlformats-officedocument.wordprocessingml.document"],
      extensions: [".docx"],
      process: readDocxFile,
    },
    {
      mimeTypes: [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ],
      extensions: [".xls", ".xlsx"],
      process: readExcelFile,
    },
    {
      mimeTypes: ["text/csv", "text/plain"],
      extensions: [".csv", ".txt", ".prn"],
      process: readTxtFile,
    },
    {
      mimeTypes: ["application/json"],
      extensions: [".json"],
      process: readJSONFile,
    },
  ]

  const findFileProcessor = (file: File): FileProcessor | null => {
    const fileType = fileTypeMap.find(
      (type) =>
        type.mimeTypes.includes(file.type) ||
        type.extensions.some((ext) => file.name.toLowerCase().endsWith(ext))
    )
    return fileType?.process ?? null
  }

  const processFiles = async (files: File[]): Promise<void> => {
    const fileContents: string[] = []
    let estimatedTokensForLargeFiles = 0

    for (const file of files) {
      try {
        // Skip heavy parsing for large files to prevent browser freeze
        if (file.size > MAX_FILE_SIZE_FOR_PARSING) {
          logger.debug("Skipping file parsing for large file, using estimate:", {
            fileName: file.name,
            fileSize: file.size,
          })
          estimatedTokensForLargeFiles += Math.ceil(file.size * ESTIMATED_TOKENS_PER_BYTE)
          continue
        }

        const processor = findFileProcessor(file)
        if (processor) {
          const content = await processor(file)
          fileContents.push(content)
        } else {
          // For unsupported types, use size-based estimate
          logger.warn("Unsupported file type for token count:", { fileType: file.type })
          estimatedTokensForLargeFiles += Math.ceil(file.size * ESTIMATED_TOKENS_PER_BYTE)
        }
      } catch (error) {
        logger.error("Error processing file:", error as Error)
        // On error, use size-based estimate
        estimatedTokensForLargeFiles += Math.ceil(file.size * ESTIMATED_TOKENS_PER_BYTE)
      }
    }

    const combinedContent = fileContents.join(" ")
    const parsedTokens = combinedContent ? tokenService.countTokens(combinedContent) : 0
    onTokenCountChange(parsedTokens + estimatedTokensForLargeFiles)
  }

  useEffect(() => {
    if (availableFiles && availableFiles.length > 0) {
      processFiles(availableFiles).catch(() => {
        logger.error("error processing files for token count")
      })
    } else {
      onTokenCountChange(0)
    }
  }, [availableFiles])
}
